import { Linkable } from '@/components/blocks/Linkable';
import { OrderItemDetails } from '@/components/content/OrderHistory/parts/History/parts/OrderItemDetails';
import {
	cardOrderSX,
	dateOrderSX,
	dividerVerticalSX,
	OrderCardHeaderOrderSX,
	linkableOrderSX,
	OrderCardHeaderTitleFrameSX,
	OrderCardContentFrameSX,
	OrderCardContentOrderLabelSX,
	OrderCardContentOrderValueSX,
	OrderCardActionsFrameSX,
	OrderCardActionDetailsFrameSX,
	OrderCardActionCartFrameSX,
	OrderCardActionStatusSX,
} from '@/components/content/OrderHistory/parts/History/style/historyOrders';
import { useOrderHistory, useOrderHistoryWithDetails } from '@/data/Content/OrderHistory';
import { Divider, Grid, Stack, Typography, Box } from '@mui/material';
import { FC, useMemo, MouseEvent } from 'react';
import { useLocalization } from '@/data/Localization';
import { ORDER_STATUS_DESCRIPTION } from '@/data/types/Order';
import { OneClick } from '@/components/blocks/OneClick';
import { useProductsWithParent } from '@/data/Content/Products';
import { OrderCardSkeleton } from '@/components/content/OrderHistory/parts/History/parts/OrderCardSkeleton';

export type OrderHistoryContextValues = ReturnType<typeof useOrderHistory> & {
	ordersWithItemsDetails: Array<any>;
};

const formatDate = (date: Date) => {
	const formatter = new Intl.DateTimeFormat('es-ES', {
		day: 'numeric',
		month: 'long',
		year: 'numeric',
	});

	return formatter.format(date).replace(/ de (\d{4})/, ', $1');
};

const useCustomDateFormatter = () => ({
	format: (date: Date) => formatDate(date),
});

type Order = ReturnType<typeof useOrderHistoryWithDetails>['ordersWithItemsDetails'][0];

export const OrderCard: FC<{
	order: Order;
	isMobile: boolean;
	onReOrder: (_e: MouseEvent<HTMLButtonElement>) => Promise<void>;
}> = ({ order, isMobile, onReOrder }) => {
	const formatterMobile = useMemo(
		() =>
			new Intl.DateTimeFormat('es-ES', {
				day: 'numeric',
				month: 'short',
				year: 'numeric',
			}),
		[]
	);
	const formatMobileDate = (dateString: string) =>
		dateString.replace(/(\d{1,2}) (\w+) (\d{4})/, '$1 $2, $3');

	const orderDateMobile = useMemo(
		() =>
			order?.placedDate
				? formatMobileDate(formatterMobile.format(new Date(order?.placedDate)))
				: '',
		[formatterMobile, order?.placedDate]
	);
	const formatter = useCustomDateFormatter();
	const orderDate = useMemo(
		() => (order?.placedDate ? formatter.format(new Date(order?.placedDate)) : ''),
		[formatter, order?.placedDate]
	);
	const routes = useLocalization('Routes');

	const partNumber = useMemo(() => {
		if (!order?.orderDetailsItems?.orderItem?.length) {
			return [];
		}

		return order?.orderDetailsItems?.orderItem?.map((item) => item.partNumber) ?? [];
	}, [order?.orderDetailsItems?.orderItem]);

	const { products } = useProductsWithParent({
		partNumber,
	});

	if (!products.length) {
		return <OrderCardSkeleton />;
	}

	return (
		<Stack sx={cardOrderSX}>
			<Stack direction="row" sx={OrderCardHeaderOrderSX}>
				<Box sx={{ display: 'flex', gap: '4px' }}>
					<Stack sx={OrderCardHeaderTitleFrameSX}>
						<Typography
							component={'h2'}
							sx={OrderCardActionStatusSX(isMobile, order.orderStatus ?? 'unknown')}
						>
							{ORDER_STATUS_DESCRIPTION[order.orderStatus as keyof typeof ORDER_STATUS_DESCRIPTION]}
						</Typography>
					</Stack>
				</Box>

				{!isMobile ? (
					<Stack direction="row" sx={OrderCardContentFrameSX}>
						<Stack direction="row">
							<Typography component={'h3'} sx={OrderCardContentOrderLabelSX}>
								{'Orden N°'}
							</Typography>
							<Typography sx={OrderCardContentOrderValueSX}>{order.orderId}</Typography>
						</Stack>
						<Divider orientation="vertical" flexItem sx={dividerVerticalSX} />
						<Typography sx={dateOrderSX}>{orderDate}</Typography>
						<Divider orientation="vertical" flexItem sx={dividerVerticalSX} />
						<Linkable
							type="button-base"
							component="a"
							disableRipple
							sx={linkableOrderSX}
							href={{
								pathname: routes.OrderDetails.route.t(),
								query: { orderId: order?.orderId },
							}}
						>
							{'Ver detalles'}
						</Linkable>
						<Divider orientation="vertical" flexItem sx={dividerVerticalSX} />
						<OneClick
							wrapper="button-base"
							sx={linkableOrderSX}
							disableRipple
							component="a"
							onClick={onReOrder}
						>
							{'Agregar todo al carrito'}
						</OneClick>
					</Stack>
				) : (
					<Typography sx={dateOrderSX}>{orderDateMobile}</Typography>
				)}
			</Stack>

			<Box
				sx={{
					maxHeight: products.length > 3 ? '400px' : 'auto',
					overflowY: products.length > 3 ? 'auto' : 'visible',
				}}
			>
				{products.map((product) => (
					<OrderItemDetails
						key={product.id}
						product={product}
						length={products.length}
						currency="MXN"
						orderItemPrice={+product.price[0].value}
					/>
				))}
			</Box>

			{isMobile ? (
				<Grid container sx={OrderCardActionsFrameSX}>
					<Grid item xs={6} sx={OrderCardActionDetailsFrameSX}>
						<Linkable
							sx={linkableOrderSX}
							wrapper="button-base"
							variant="outlined"
							data-testid="order-details"
							id="order-details"
							href={{
								pathname: routes.OrderDetails.route.t(),
								query: { orderId: order?.orderId },
							}}
						>
							{'Ver detalles'}
						</Linkable>
					</Grid>
					<Grid item xs={6} sx={OrderCardActionCartFrameSX}>
						<OneClick
							wrapper="button-base"
							sx={linkableOrderSX}
							disableRipple
							component="a"
							onClick={onReOrder}
						>
							{'Agregar todo al carrito'}
						</OneClick>
					</Grid>
				</Grid>
			) : null}
		</Stack>
	);
};
